/**
 * Wirtschaftsplan-Editor — Phase 3.
 * Route: /weg/wirtschaftsplan/:planId
 *
 * - Positionen hinzufügen/entfernen/bearbeiten
 * - Konto wählen → Standard-Verteilungsschlüssel wird vorgeschlagen (Override möglich)
 * - Live-Berechnung der Verteilung pro Einheit
 * - Genehmigen (draft → approved)
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, Plus, Trash, Calculator, Warning, CheckCircle, ReceiptX,
} from "@phosphor-icons/react";

const KEY_LABEL = {
  mea: "MEA", head: "Kopf", consumption: "Verbrauch", area: "Fläche", none: "Keine",
};
const eur = (v) => (v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2 });

export default function WirtschaftsplanDetail() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [calc, setCalc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = () => {
    api.get(`/weg/economic-plans/${planId}`).then((r) => setPlan(r.data));
    api.get("/weg/accounts").then((r) => setAccounts(r.data || []));
  };
  useEffect(() => { load(); }, [planId]); // eslint-disable-line react-hooks/exhaustive-deps

  const accountMap = useMemo(() => Object.fromEntries(accounts.map((a) => [a.id, a])), [accounts]);
  const isEditable = plan && plan.status === "draft";

  const setPositions = (positions) => { setPlan({ ...plan, positions }); setDirty(true); };

  const addPosition = () => {
    setPositions([...(plan.positions || []), {
      account_id: "", amount_annual: 0, distribution_key: "mea", consumption_data: {},
    }]);
  };
  const removePosition = (idx) => {
    setPositions(plan.positions.filter((_, i) => i !== idx));
  };
  const updatePosition = (idx, field, value) => {
    const next = [...plan.positions];
    next[idx] = { ...next[idx], [field]: value };
    // Wenn Konto gewählt → default distribution_key übernehmen (initial)
    if (field === "account_id") {
      const a = accountMap[value];
      if (a) {
        next[idx].distribution_key = a.default_distribution_key || "mea";
        next[idx].account_number = a.account_number;
        next[idx].account_name = a.name;
      }
    }
    setPositions(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/weg/economic-plans/${planId}`, {
        property_id: plan.property_id,
        year: plan.year,
        title: plan.title,
        status: plan.status,
        resolution_id: plan.resolution_id,
        notes: plan.notes,
        positions: (plan.positions || []).map((p) => ({
          account_id: p.account_id,
          amount_annual: Number(p.amount_annual) || 0,
          distribution_key: p.distribution_key,
          consumption_data: p.consumption_data || {},
          notes: p.notes || null,
        })),
      });
      toast.success("Wirtschaftsplan gespeichert");
      setDirty(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const calculate = async () => {
    if (dirty) await save();
    const r = await api.post(`/weg/economic-plans/${planId}/calculate`);
    setCalc(r.data);
    toast.success("Verteilung berechnet");
  };

  const approve = async () => {
    try {
      await api.post(`/weg/economic-plans/${planId}/approve`, {});
      toast.success("Wirtschaftsplan genehmigt");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Genehmigung fehlgeschlagen");
    }
  };

  if (!plan) return <div className="p-6 text-sm text-slate-500">Lade …</div>;

  return (
    <div className="space-y-6" data-testid="wp-detail-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs">
            <Link to={`/objekte/${plan.property_id}?tab=weg`} className="text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
              <ArrowLeft size={12} /> Zurück zum Objekt
            </Link>
          </div>
          <h1 className="text-2xl font-semibold text-slate-900 mt-1">
            Wirtschaftsplan {plan.year}
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge className={`text-[10px] ${plan.status === "approved" ? "bg-emerald-100 text-emerald-800" : plan.status === "closed" ? "bg-slate-100 text-slate-700" : "bg-amber-100 text-amber-800"}`}>
              {plan.status === "draft" ? "Entwurf" : plan.status === "approved" ? "Genehmigt" : "Geschlossen"}
            </Badge>
            {plan.title && <span className="text-sm text-slate-600">· {plan.title}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEditable && (
            <>
              <Button variant="outline" size="sm" onClick={save} disabled={!dirty || saving} data-testid="wp-save-btn">
                Speichern
              </Button>
              <Button variant="secondary" size="sm" onClick={calculate} data-testid="wp-calculate-btn">
                <Calculator size={13} className="mr-1.5" /> Berechnen
              </Button>
              <Button size="sm" onClick={approve} data-testid="wp-approve-btn">
                <CheckCircle size={13} className="mr-1.5" /> Genehmigen
              </Button>
            </>
          )}
          {!isEditable && (
            <Button variant="secondary" size="sm" onClick={calculate}>
              <Calculator size={13} className="mr-1.5" /> Berechnen
            </Button>
          )}
        </div>
      </div>

      {/* Positionen */}
      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-medium text-slate-800">Positionen</h2>
          {isEditable && (
            <Button size="sm" variant="outline" onClick={addPosition} data-testid="wp-add-position">
              <Plus size={13} className="mr-1.5" /> Position
            </Button>
          )}
        </div>
        {(plan.positions || []).length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500 bg-slate-50">
            Noch keine Positionen. {isEditable && "Klicke oben rechts auf ‚Position' um zu starten."}
          </div>
        ) : (
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Konto</th>
                  <th className="text-right px-3 py-2 w-32">Jahresbetrag</th>
                  <th className="text-center px-3 py-2 w-28">Verteilung</th>
                  {isEditable && <th className="w-10"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {plan.positions.map((p, idx) => {
                  const a = accountMap[p.account_id];
                  return (
                    <tr key={idx} data-testid={`wp-position-row-${idx}`}>
                      <td className="px-3 py-2">
                        {isEditable ? (
                          <Select value={p.account_id || "__none__"} onValueChange={(v) => updatePosition(idx, "account_id", v === "__none__" ? "" : v)}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`wp-position-account-${idx}`}>
                              <SelectValue placeholder="Konto wählen …" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">— Konto wählen —</SelectItem>
                              {accounts.filter((x) => x.is_active !== false).map((x) => (
                                <SelectItem key={x.id} value={x.id}>{x.account_number} · {x.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <div className="text-sm">
                            <span className="font-mono text-xs text-slate-500 mr-2">{a?.account_number || p.account_number}</span>
                            {a?.name || p.account_name}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {isEditable ? (
                          <Input
                            type="number" min="0" step="0.01"
                            className="h-8 text-xs text-right w-28 ml-auto"
                            value={p.amount_annual}
                            onChange={(e) => updatePosition(idx, "amount_annual", e.target.value)}
                            data-testid={`wp-position-amount-${idx}`}
                          />
                        ) : (
                          <span className="font-mono text-sm">{eur(Number(p.amount_annual))}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {isEditable ? (
                          <Select value={p.distribution_key} onValueChange={(v) => updatePosition(idx, "distribution_key", v)}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`wp-position-key-${idx}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(KEY_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge variant="outline" className="text-[10px]">{KEY_LABEL[p.distribution_key]}</Badge>
                        )}
                      </td>
                      {isEditable && (
                        <td className="px-1">
                          <Button variant="ghost" size="sm" onClick={() => removePosition(idx)} data-testid={`wp-position-remove-${idx}`}>
                            <Trash size={13} className="text-rose-600" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              {plan.positions.length > 0 && (
                <tfoot className="bg-slate-50 text-xs">
                  <tr>
                    <td className="px-3 py-2 font-semibold text-slate-700">Summe (Ausgaben)</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold" data-testid="wp-total-expense">
                      {eur(plan.positions.filter((p) => (accountMap[p.account_id]?.kind || "expense") === "expense").reduce((s, p) => s + Number(p.amount_annual || 0), 0))}
                    </td>
                    <td colSpan={isEditable ? 2 : 1}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </section>

      {/* Verbrauchsdaten pro Position mit key=consumption */}
      {isEditable && plan.positions?.some((p) => p.distribution_key === "consumption") && (
        <section className="space-y-2">
          <h2 className="text-base font-medium text-slate-800">Verbrauchsdaten pro Einheit</h2>
          <p className="text-xs text-slate-500">Bei Positionen mit Verbrauchs-Verteilung: pro Einheit den Verbrauchswert (z. B. m³ Wasser) eintragen.</p>
          {plan.positions.map((p, idx) => (
            p.distribution_key === "consumption" && (
              <ConsumptionEditor
                key={idx}
                position={p}
                units={calc?.by_unit || []}
                onChange={(cdata) => updatePosition(idx, "consumption_data", cdata)}
                accountLabel={accountMap[p.account_id]?.name || `Position ${idx + 1}`}
              />
            )
          ))}
        </section>
      )}

      {/* Ergebnis */}
      {calc && (
        <section className="space-y-3" data-testid="wp-result">
          <h2 className="text-base font-medium text-slate-800">Verteilung pro Einheit</h2>
          {calc.warnings?.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <div className="font-medium mb-1"><Warning size={12} weight="fill" className="inline mr-1" /> Hinweise</div>
              <ul className="list-disc pl-5 space-y-0.5">{calc.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
            </div>
          )}
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Einheit</th>
                  <th className="text-right px-3 py-2">MEA</th>
                  <th className="text-right px-3 py-2">Wohnfläche</th>
                  <th className="text-right px-3 py-2">Jahr</th>
                  <th className="text-right px-3 py-2">Monatlich</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {calc.by_unit.map((u) => (
                  <tr key={u.unit_id} data-testid={`wp-unit-${u.unit_number}`}>
                    <td className="px-3 py-2 font-mono text-xs">{u.unit_number}</td>
                    <td className="px-3 py-2 text-right text-xs">{u.weg_share || "—"}</td>
                    <td className="px-3 py-2 text-right text-xs">{u.living_area ? `${u.living_area} m²` : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono text-sm">{eur(u.total_year)}</td>
                    <td className="px-3 py-2 text-right font-mono text-sm font-semibold">{eur(u.total_month)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 text-xs">
                <tr>
                  <td colSpan={3} className="px-3 py-2 font-semibold text-slate-700">Gesamt (Ausgaben − Einnahmen)</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">{eur(calc.totals.expense - calc.totals.income)}</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">{eur((calc.totals.expense - calc.totals.income) / 12)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="text-xs text-slate-500 flex items-center gap-4">
            <span>Einnahmen: <b>{eur(calc.totals.income)}</b></span>
            <span>Ausgaben: <b>{eur(calc.totals.expense)}</b></span>
            <span>Rücklagen-Zuführung: <b>{eur(calc.totals.reserve_addition)}</b></span>
          </div>
        </section>
      )}
    </div>
  );
}

function ConsumptionEditor({ position, units, onChange, accountLabel }) {
  const cdata = position.consumption_data || {};
  const [local, setLocal] = useState(cdata);
  useEffect(() => setLocal(position.consumption_data || {}), [position.consumption_data]);
  const setValue = (uid, v) => {
    const next = { ...local, [uid]: v };
    setLocal(next);
    onChange(next);
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3">
      <div className="text-sm font-medium text-slate-700 mb-2">
        <ReceiptX size={13} className="inline mr-1.5" /> {accountLabel}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {units.map((u) => (
          <label key={u.unit_id} className="text-xs text-slate-600 flex items-center gap-2">
            <span className="font-mono text-[11px] w-24 flex-shrink-0">{u.unit_number}</span>
            <Input
              type="number" min="0" step="0.01"
              className="h-7 text-xs"
              value={local[u.unit_id] || ""}
              onChange={(e) => setValue(u.unit_id, Number(e.target.value) || 0)}
              placeholder="0"
            />
          </label>
        ))}
      </div>
      {units.length === 0 && (
        <p className="text-xs text-slate-500 italic">Zuerst {'"'}Berechnen{'"'} klicken, um Einheiten zu laden.</p>
      )}
    </div>
  );
}
